import { notFound, useLoaderData } from '@tanstack/react-router'

import LegalLayout from '@/components/LegalLayout'
import { Markdown } from '@/components/Markdown'
import { formatDate, type LegalSlug } from '@/lib/cms/cms'
import type { LegalPageContent } from '@/lib/cms/queries.server'
import { getLegalPage } from '@/lib/cms/server'
import { pageLinks, pageMeta } from '@/lib/seo'

/**
 * The one definition of a legal page route. The four route files (terms,
 * privacy, acceptable-use, cancellation) are each a two-line
 * `createFileRoute('/x')(legalRouteOptions('x'))` — file-based routing needs
 * the files to exist, but the shell (loader, head, layout) must not be
 * copy-pasted four times: LEGAL_SLUGS in lib/cms/cms.ts is the list to edit
 * when a page is added, and this factory is what makes that list meaningful.
 */
export function legalRouteOptions(slug: LegalSlug) {
  const path = `/${slug}`

  function LegalPage() {
    // strict:false because the factory can't name the route id generically;
    // the shape is guaranteed by the loader below.
    const page = useLoaderData({ strict: false }) as LegalPageContent
    return (
      <LegalLayout
        title={page.title}
        effectiveDate={
          page.effectiveDate ? formatDate(page.effectiveDate) : null
        }
      >
        <Markdown>{page.bodyMd}</Markdown>
      </LegalLayout>
    )
  }

  return {
    loader: async (): Promise<LegalPageContent> => {
      const page = await getLegalPage({ data: { slug } })
      if (!page) throw notFound()
      return page
    },
    head: ({ loaderData }: { loaderData?: LegalPageContent }) =>
      loaderData
        ? {
            meta: pageMeta({
              title: `${loaderData.title} · Masklabs`,
              description: loaderData.description,
              path,
            }),
            links: pageLinks(path),
          }
        : {},
    component: LegalPage,
  }
}
